import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const DOC_GEN_STEPS = [
  { key: "queued", label: "Queued" },
  { key: "template_download", label: "Downloading Templates..." },
  { key: "doc_fill", label: "Filling Documents..." },
  { key: "pdf_conversion", label: "Converting to PDF..." },
  { key: "merge", label: "Merging Documents..." },
  { key: "ams", label: "Creating Policy..." },
  { key: "upload_and_notify", label: "Uploading & Notifying..." },
];

function getStepIndex(step) {
  const idx = DOC_GEN_STEPS.findIndex((s) => s.key === step);
  return idx === -1 ? 0 : idx;
}

function getDocGenDotColor(i, activeIdx) {
  if (i < activeIdx) return "success.main";
  if (i === activeIdx) return "info.main";
  return "grey.300";
}

function DocGenProgress({ step }) {
  const activeIdx = getStepIndex(step);
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {DOC_GEN_STEPS.map((s, i) => (
        <Box
          key={s.key}
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: getDocGenDotColor(i, activeIdx),
            animation: i === activeIdx ? "pulse 1.4s infinite" : "none",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.3 },
            },
          }}
        />
      ))}
    </Stack>
  );
}

DocGenProgress.propTypes = {
  step: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

function formatDuration(minutes) {
  if (minutes == null) return "-";
  if (minutes < 1) return "<1m";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function formatApprovedAt(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function rowBondNumber(row) {
  return row.bond_number ?? row.bondNumber ?? "-";
}

function rowName(row) {
  return row.name ?? row.estate_name ?? row.bond_name ?? "-";
}

function rowType(row) {
  return row.bond_type ?? row.type ?? "-";
}

function rowTriggeredBy(row) {
  return row.triggered_by ?? row.triggered_by_name ?? row.approved_by ?? "-";
}

function rowApprovedAt(row) {
  return row.approved_at ?? row.approvedAt;
}

function rowStatus(row) {
  return (row.doc_generation_status ?? row.status ?? "").toString().toLowerCase();
}

function rowDurationMinutes(row) {
  const v = row.minutes_elapsed ?? row.duration_minutes ?? row.duration;
  if (typeof v === "number") return v;
  if (v != null) return Number(v);
  return null;
}

function rowStep(row) {
  return row.current_step ?? row.step ?? "queued";
}

function renderDocGenRow(item, idx) {
  const st = rowStatus(item);
  const processing = st === "processing";
  const completed = st === "completed";
  const failed = st === "failed";
  const step = rowStep(item);
  const stepLabel = DOC_GEN_STEPS[getStepIndex(step)]?.label || "Processing...";
  const isQueued = step === "queued" || !step;

  return (
    <TableRow key={item.id ?? item.bond_id ?? idx}>
      <TableCell>{rowBondNumber(item)}</TableCell>
      <TableCell>{rowName(item)}</TableCell>
      <TableCell>{rowType(item)}</TableCell>
      <TableCell>{rowTriggeredBy(item)}</TableCell>
      <TableCell>{formatApprovedAt(rowApprovedAt(item))}</TableCell>
      <TableCell>
        {processing && (
          <Chip
            size="small"
            color={isQueued ? "warning" : "info"}
            icon={<CircularProgress size={14} color="inherit" />}
            label={stepLabel}
            sx={{ "& .MuiChip-icon": { ml: 0.5 } }}
          />
        )}
        {completed && (
          <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Completed" />
        )}
        {failed && (
          <Chip size="small" color="error" icon={<ErrorOutlineIcon />} label="Failed" />
        )}
        {!processing && !completed && !failed && (
          <Chip size="small" label={(item.status ?? st) || "—"} />
        )}
      </TableCell>
      <TableCell>
        {processing ? (
          <DocGenProgress step={step} />
        ) : (
          formatDuration(rowDurationMinutes(item))
        )}
      </TableCell>
    </TableRow>
  );
}

export default function ApproveBondsStatsDialog({
  statsOpen,
  setStatsOpen,
  dataLoading,
  stats,
  formatTime,
  docGenGroups = [],
}) {
  const docGenFlat = useMemo(
    () => docGenGroups.flatMap((g) => (Array.isArray(g.items) ? g.items : [])),
    [docGenGroups]
  );

  const processingCount = docGenFlat.filter((row) => rowStatus(row) === "processing").length;

  return (
    <Dialog open={statsOpen} onClose={() => setStatsOpen(false)} maxWidth="lg" fullWidth>
      <DialogTitle>Approval statistics</DialogTitle>
      <DialogContent>
        {dataLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography variant="body1">
              Total approved bonds: <strong>{stats?.totalApprovedBonds ?? 0}</strong>
            </Typography>
            <Typography variant="body1">
              Average processing time:{" "}
              <strong>{formatTime?.(stats?.averageProcessingTime ?? 0) ?? "—"}</strong>
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h6">Document generation status</Typography>
          {docGenFlat.length > 0 && processingCount > 0 && (
            <Chip size="small" color="warning" variant="outlined" label={`${processingCount} active`} />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          See where each bond is across every step of document generation, from start to finish.
          Updates in real time; entries stay visible for 7 days.
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Bond #</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Triggered By</TableCell>
                <TableCell>Approved At</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {docGenFlat.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      No document generation jobs in progress.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                docGenGroups.map((group) => (
                  <React.Fragment key={group.date || group.display_date || "group"}>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ bgcolor: "action.hover", py: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {group.display_date || group.date || "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {(group.items || []).map((item, idx) => renderDocGenRow(item, idx))}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setStatsOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

ApproveBondsStatsDialog.propTypes = {
  statsOpen: PropTypes.bool.isRequired,
  setStatsOpen: PropTypes.func.isRequired,
  dataLoading: PropTypes.bool.isRequired,
  stats: PropTypes.object,
  formatTime: PropTypes.func,
  docGenGroups: PropTypes.array,
};
